/**
 * Review workspace API contract — the FE/BE interface for this feature.
 *
 * Conventions:
 *   • IDs are the branded types exported from `../review-data`.
 *   • Timestamps are ISO 8601 strings, always UTC.
 *   • Every mutation returns the updated `Review` snapshot so clients can
 *     reconcile optimistic state without a follow-up refetch. `updatedAt`
 *     is authoritative from the server.
 *   • Errors follow RFC 7807 (application/problem+json); see `ApiError`.
 */

import type {
  Finding,
  FindingAxes,
  FindingId,
  Message,
  MessageId,
  Review,
  ReviewId,
  UserId,
} from "../data/review-data"

/* ── Read side ─────────────────────────────────────────────────────────── */

export interface GetReviewInput {
  reviewId: ReviewId
  /** Opaque cursor for paging the messages sub-collection. */
  messagesCursor?: string
}

export type GetReviewResponse = Review

/* ── Verdict / state axes ──────────────────────────────────────────────── */

export interface SetVerdictInput {
  reviewId: ReviewId
  findingId: FindingId
  verdict: FindingAxes["verdict"]
}

export interface MarkCorrectedInput {
  reviewId: ReviewId
  findingId: FindingId
}

export interface EditFindingNarrativeInput {
  reviewId: ReviewId
  findingId: FindingId
  patch: {
    claim?: string
    reasoning?: string | null
    remediation?: string | null
  }
}

/* ── Manual add / remove ───────────────────────────────────────────────── */

export interface AddFindingInput {
  reviewId: ReviewId
  ruleId: string
  severity: "error" | "warning" | "info"
  claim: string
  reasoning: string | null
  remediation: string | null
  authorities: Array<{ kind: "cfr" | "lrm" | "statute" | "prn" | "internal"; label: string }>
  bbox: { page: number; x0: number; y0: number; x1: number; y1: number } | null
  pageHint: number | null
  groupId: "g-text" | "g-format"
  divergence: { master: string | null; printed: string | null } | null
}

export interface AddFindingResponse {
  finding: Finding
  review: Review
}

export interface RemoveFindingInput {
  reviewId: ReviewId
  findingId: FindingId
}

/* ── Assignment / routing ──────────────────────────────────────────────── */

export interface AssignReviewInput {
  reviewId: ReviewId
  assigneeId: UserId
  note: string | null
}

export interface ReturnToPoolInput {
  reviewId: ReviewId
}

export interface AdvanceStageInput {
  reviewId: ReviewId
}

export interface SkipSecondPassInput {
  reviewId: ReviewId
}

export interface ArchiveReviewInput {
  reviewId: ReviewId
}

/* ── Messages ──────────────────────────────────────────────────────────── */

export interface PostMessageInput {
  reviewId: ReviewId
  body: string
  findingId: FindingId | null
}

export interface PostMessageResponse {
  message: Message
  review: Review
}

export interface MarkMessagesReadInput {
  reviewId: ReviewId
  findingId: FindingId | null
}

/* ── Every mutation shares this response shape ─────────────────────────── */

export type MutationResponse = { review: Review }

/* ── Error model (RFC 7807) ────────────────────────────────────────────── */

export type ApiErrorCode =
  | "not_found"
  | "unauthorized"
  | "forbidden"
  | "conflict"
  | "validation_failed"
  | "rate_limited"
  | "server_error"
  | "network"

export class ApiError extends Error {
  readonly code: ApiErrorCode
  readonly status: number
  /** Field-level errors when `code === "validation_failed"`. */
  readonly fieldErrors: Record<string, string> | null
  /** Retry hint in seconds when `code === "rate_limited"`. */
  readonly retryAfterSeconds: number | null

  constructor(
    code: ApiErrorCode,
    message: string,
    status: number,
    opts: {
      fieldErrors?: Record<string, string> | null
      retryAfterSeconds?: number | null
    } = {},
  ) {
    super(message)
    this.name = "ApiError"
    this.code = code
    this.status = status
    this.fieldErrors = opts.fieldErrors ?? null
    this.retryAfterSeconds = opts.retryAfterSeconds ?? null
  }
}

/* ── Live channel envelope (WS invalidation only, per playbook) ────────── */

export type LiveFrame =
  | { kind: "review.updated"; reviewId: ReviewId; at: string }
  | { kind: "finding.updated"; reviewId: ReviewId; findingId: FindingId; at: string }
  | { kind: "message.created"; reviewId: ReviewId; messageId: MessageId; at: string }
  | {
      kind: "presence.viewers"
      reviewId: ReviewId
      viewers: Array<{ userId: UserId; role: string }>
      at: string
    }
  | {
      kind: "presence.cursor"
      reviewId: ReviewId
      userId: UserId
      /** Cursor coordinates in PDF-point space, per-page. */
      page: number
      x: number
      y: number
      at: string
    }
